import { CLIENT_COLOR_PALETTE } from '@fos/shared/contracts/crm';
import { CLIENT_COLOR_LABELS } from '@fos/shared/labels';
import { Check, CircleAlert } from 'lucide-react';
import { type CSSProperties, useId, useState } from 'react';
import { cn } from '../../../lib/cn';
import { Input } from '../../../ui/Input';

const HEX_RE = /^#[0-9A-F]{6}$/;

/** „2b3bff”, „#2b3bff” → „#2B3BFF”; niepełny tekst zostaje bez zmian (walidacja przy zapisie). */
export function normalizeHex(value: string): string {
  const v = value.trim().toUpperCase();
  if (!v) return '';
  return v.startsWith('#') ? v : `#${v}`;
}

export function isValidHex(value: string): boolean {
  return HEX_RE.test(normalizeHex(value));
}

/**
 * Kolor klienta: paleta z DESIGN §3 (pięć kolorów klientów), „bez koloru” i dowolny kolor #RRGGBB.
 * Natywne pola radio w grupie (klawiatura i czytniki ekranu), wyglądające jak próbki koloru.
 */
export function ClientColorField({
  value,
  onChange,
  error,
}: {
  value: string | null;
  onChange: (value: string | null) => void;
  error?: string | null;
}) {
  const inPalette = value !== null && (CLIENT_COLOR_PALETTE as readonly string[]).includes(value.toUpperCase());
  const [custom, setCustom] = useState(() => (value && !inPalette ? value : ''));
  const name = useId();
  const customId = useId();
  const errorId = useId();

  const swatch = (color: string | null, label: string) => {
    const selected = color === null ? value === null : value?.toUpperCase() === color;
    const style = color ? ({ backgroundColor: color } as CSSProperties) : undefined;
    return (
      <label
        key={color ?? 'none'}
        title={label}
        style={style}
        className={cn(
          'relative inline-flex size-7 cursor-pointer items-center justify-center rounded-full transition-shadow',
          'has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-accent',
          color === null && 'border border-dashed border-line-strong bg-canvas',
          selected && 'ring-2 ring-fg ring-offset-2 ring-offset-canvas',
        )}
      >
        <input
          type="radio"
          name={name}
          className="sr-only"
          checked={selected}
          aria-label={label}
          onChange={() => {
            onChange(color);
            if (color !== null) setCustom('');
          }}
        />
        {selected && color && <Check size={14} strokeWidth={2.5} aria-hidden className="text-white" />}
      </label>
    );
  };

  return (
    <fieldset className="flex flex-col gap-2.5" aria-describedby={error ? errorId : undefined}>
      <legend className="mb-1.5 text-caption font-medium text-fg-secondary">Kolor</legend>
      <div className="flex flex-wrap items-center gap-2.5">
        {CLIENT_COLOR_PALETTE.map((c) => swatch(c, CLIENT_COLOR_LABELS[c] ?? c))}
        {swatch(null, 'Bez koloru')}
      </div>
      <div className="flex items-center gap-2">
        <label htmlFor={customId} className="shrink-0 text-caption text-fg-muted">
          Inny kolor
        </label>
        <div className="w-[132px]">
          <Input
            id={customId}
            size="s"
            mono
            placeholder="#RRGGBB"
            maxLength={7}
            value={custom}
            aria-invalid={Boolean(error) || undefined}
            onChange={(e) => {
              const next = e.target.value;
              setCustom(next);
              if (isValidHex(next)) onChange(normalizeHex(next));
            }}
            onBlur={() => {
              if (custom && isValidHex(custom)) setCustom(normalizeHex(custom));
            }}
          />
        </div>
        {custom && isValidHex(custom) && (
          <span
            aria-hidden
            className="size-5 shrink-0 rounded-full"
            style={{ backgroundColor: normalizeHex(custom) } as CSSProperties}
          />
        )}
      </div>
      {error && (
        <p id={errorId} role="alert" className="flex items-start gap-1.5 text-caption text-danger">
          <CircleAlert size={14} strokeWidth={1.75} aria-hidden className="mt-px shrink-0" />
          <span>{error}</span>
        </p>
      )}
    </fieldset>
  );
}
